import { dateTime, money, paymentMethodLabel } from '../lib/format';
import type { Order } from '../types';

// Pay-now bills are paid at the bill's own timestamp; pay-later ones later.
function sameInstant(a: string, b: string): boolean {
  return a === b || new Date(a).getTime() === new Date(b).getTime();
}

/**
 * Printable 80mm-style receipt. Purely presentational — it renders the
 * server-computed values from the Order DTO. No bill math is done here.
 * A GST (company) bill prints as a TAX INVOICE with a "Bill To" block.
 */
export function Receipt({
  order,
  footer,
  gstin,
}: {
  order: Order;
  footer?: string;
  gstin?: string | null;
}) {
  const gst = order.gstDetails;
  // A tax invoice must carry the supplier's (store's) GSTIN.
  const storeGstin = gstin ?? (gst ? order.storeGstin : null);
  const paymentDue =
    order.status === 'COMPLETED' && order.paymentStatus === 'UNPAID';
  // Only a GST bill can be paid later. (Older bills' payments were stamped a
  // moment after the bill, so the time check alone would misfire on them.)
  const paidLater =
    gst != null &&
    order.paidAt != null &&
    (order.paymentReference != null ||
      !sameInstant(order.paidAt, order.createdAt));

  return (
    <div className="receipt print-area">
      <div className="center">
        <div className="r-title">1885 NY SMASH BURGERS</div>
        <div>{order.storeName}</div>
        {/* The supplier's address is required on a tax invoice. */}
        {order.storeAddress && (
          <div style={{ whiteSpace: 'pre-line' }}>{order.storeAddress}</div>
        )}
        {storeGstin && <div>GSTIN: {storeGstin}</div>}
        {gst && <div style={{ fontWeight: 700, marginTop: 4 }}>TAX INVOICE</div>}
      </div>
      <hr />
      <div className="r-row">
        <span>Bill</span>
        <strong>{order.billNumber}</strong>
      </div>
      <div className="r-row">
        <span>Date</span>
        <span>{dateTime(order.createdAt)}</span>
      </div>
      <div className="r-row">
        <span>Cashier</span>
        <span>{order.cashierName}</span>
      </div>
      <div className="r-row">
        <span>Type</span>
        <span>{order.orderType.replace('_', '-')}</span>
      </div>
      {order.tableNumber && (
        <div className="r-row">
          <span>Table</span>
          <span>{order.tableNumber}</span>
        </div>
      )}
      {order.customerMobile && (
        <div className="r-row">
          <span>Mobile</span>
          <span>{order.customerMobile}</span>
        </div>
      )}
      {gst && (
        <>
          <hr />
          <div style={{ fontWeight: 700 }}>Bill To</div>
          <div>{gst.customerLegalName}</div>
          <div>GSTIN: {gst.customerGstin}</div>
          <div style={{ whiteSpace: 'pre-line' }}>{gst.billingAddress}</div>
          {gst.customerState && <div>State: {gst.customerState}</div>}
          {gst.deliveryAddress && (
            <div style={{ whiteSpace: 'pre-line' }}>
              Deliver to: {gst.deliveryAddress}
            </div>
          )}
          {gst.placeOfSupply && (
            <div className="r-row">
              <span>Place of supply</span>
              <span>{gst.placeOfSupply}</span>
            </div>
          )}
          <div className="r-row">
            <span>SAC</span>
            <span>{gst.sacCode}</span>
          </div>
        </>
      )}
      <hr />
      <table>
        <thead>
          <tr>
            <td>Item</td>
            <td style={{ textAlign: 'center' }}>Qty</td>
            <td style={{ textAlign: 'right' }}>Amt</td>
          </tr>
        </thead>
        <tbody>
          {order.items.map((it, i) => (
            <tr key={i}>
              <td>
                {it.productName}
                <br />
                <span style={{ fontSize: 11 }}>@ {money(it.unitPrice)}</span>
              </td>
              <td style={{ textAlign: 'center' }}>{it.quantity}</td>
              <td style={{ textAlign: 'right' }}>{money(it.lineTotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <hr />
      <div className="r-row">
        <span>Subtotal</span>
        <span>{money(order.subtotal)}</span>
      </div>
      {order.discountAmount > 0 && (
        <div className="r-row">
          <span>Discount</span>
          <span>- {money(order.discountAmount)}</span>
        </div>
      )}
      <div className="r-row">
        <span>Taxable</span>
        <span>{money(order.taxableAmount)}</span>
      </div>
      <div className="r-row">
        <span>CGST ({order.cgstRate}%)</span>
        <span>{money(order.cgstAmount)}</span>
      </div>
      <div className="r-row">
        <span>SGST ({order.sgstRate}%)</span>
        <span>{money(order.sgstAmount)}</span>
      </div>
      <hr />
      <div className="r-row r-total">
        <span>TOTAL</span>
        <span>{money(order.grandTotal)}</span>
      </div>
      {paymentDue ? (
        <div className="center" style={{ fontWeight: 700 }}>
          PAYMENT DUE
        </div>
      ) : (
        <>
          <div className="r-row">
            <span>Payment</span>
            <span>{paymentMethodLabel(order.paymentMethod)}</span>
          </div>
          {paidLater && (
            <div className="r-row">
              <span>Paid on</span>
              <span>{dateTime(order.paidAt)}</span>
            </div>
          )}
          {order.paymentReference && (
            <div className="r-row">
              <span>Ref</span>
              <span>{order.paymentReference}</span>
            </div>
          )}
        </>
      )}
      {order.cashReceived != null && (
        <div className="r-row">
          <span>Cash</span>
          <span>{money(order.cashReceived)}</span>
        </div>
      )}
      {order.changeReturned != null && (
        <div className="r-row">
          <span>Change</span>
          <span>{money(order.changeReturned)}</span>
        </div>
      )}
      {order.status === 'CANCELLED' && (
        <>
          <hr />
          <div className="center" style={{ fontWeight: 700 }}>
            *** CANCELLED ***
          </div>
        </>
      )}
      <hr />
      <div className="center">{footer || 'Thank you! Visit again.'}</div>
    </div>
  );
}
